import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { pipe } from 'fp-ts/es6/function'
import * as TE from 'fp-ts/es6/TaskEither'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { weblabs } from '../weblabs'
import { GetTreatmentsQueryResult, useGetTreatmentsQuery } from '../../../generated/jamsession.graphql'
import { generateSessionID, SessionID } from '../types/id-types'
import { createAAPNApolloClient } from '@amzn/aapn-front-end-apollo-client'
import { createJamSessionProvider, WeblabInitFn, WeblabInitResult } from './weblabs_provider'
import { Treatment, WeblabTreatment } from '../types/weblab-types'
import { LocalStorageWeblabCache } from '../cache/weblab_local_storage_cache'
import { CrowdSurfConfig, CrowdSurfConfigContext } from '../../appconfig'
import { getMetricsPublisherChainAsync } from '@amzn/aapn-front-end-metrics'
import { cookieWeblabsInit } from '../init/weblabs_cookie_init'

const OBFUSCATED_MARKETPLACE_ID = 'ATVPDKIKX0DER'

const createWeblabInit = (weblabTreatments: WeblabTreatment[]): WeblabInitFn => {
  return async (principal: string): Promise<WeblabInitResult> => {
    const cookieTreatments = await cookieWeblabsInit(principal)

    const weblabMap = new Map<string, WeblabTreatment>()
    weblabTreatments.forEach(weblabTreatment => weblabMap.set(weblabTreatment.weblab, weblabTreatment))
    cookieTreatments.weblabs.forEach(weblabTreatment => weblabMap.set(weblabTreatment.weblab, weblabTreatment))

    return {
      principal: principal,
      weblabs: Array.from(weblabMap.values()).map(weblabTreatment => ({
        weblab: weblabTreatment.weblab,
        treatment: weblabTreatment.treatment,
      })),
    }
  }
}

const getTreatmentsFromQueryResult = (treatmentsResponse: GetTreatmentsQueryResult): WeblabTreatment[] =>
  treatmentsResponse.error || !treatmentsResponse.data
    ? weblabs.map(weblab => ({
        weblab,
        treatment: 'C' as Treatment,
      }))
    : treatmentsResponse.data.treatments.map(treatment => ({
        weblab: treatment.weblabName,
        treatment: treatment.treatment,
      }))

type JamSessionProviderProps = {
  principal: string
  sessionId?: SessionID
  customerId?: string
}

export const JamSessionProvider: React.FunctionComponent<React.PropsWithChildren<JamSessionProviderProps>> = (
  props: React.PropsWithChildren<JamSessionProviderProps>,
) => {
  const appConfig = useContext<CrowdSurfConfig>(CrowdSurfConfigContext)
  const [client, setClient] = useState<ApolloClient<NormalizedCacheObject> | undefined>(undefined)
  useEffect(() => {
    void pipe(createAAPNApolloClient(null, appConfig.experimentationApiEndpoint, {}), TE.map(setClient))()
  }, [appConfig.experimentationApiEndpoint])

  const treatmentsResponse: GetTreatmentsQueryResult = useGetTreatmentsQuery({
    variables: {
      weblabEntries: weblabs.map(weblab => ({
        weblabName: weblab,
        weblabContext: {
          obfuscatedMarketplaceId: OBFUSCATED_MARKETPLACE_ID,
          sessionId: props.sessionId || generateSessionID(''),
          customerId: props.customerId,
        },
      })),
    },
    ...(client && { client }),
    skip: !client,
  })
  const treatments = getTreatmentsFromQueryResult(treatmentsResponse)

  const weblabInit: WeblabInitFn = createWeblabInit(treatments)
  const JamSessionProviderImpl = useMemo(() => createJamSessionProvider(weblabInit, LocalStorageWeblabCache), [weblabInit])

  useEffect(() => {
    getMetricsPublisherChainAsync('JamSession').then(p => {
      if (!client) {
        p.publishCounter('apollo_initialization_error', 1)
      } else if (treatmentsResponse.error) {
        p.publishCounter('client_error', 1).publishString('client_error_message', treatmentsResponse.error.message)
      } else if (treatmentsResponse.data?.treatments.some(treatment => treatment.error)) {
        p.publishCounter('service_error', 1)
      } else {
        p.publishCounter('success', 1)
      }
      p.publishString(
        'weblabs',
        JSON.stringify(
          treatments.reduce((acc, exp) => {
            acc[exp.weblab] = exp.treatment
            return acc
          }, {} as Record<string, string>),
        ),
      )
    })
  }, [client, treatments, treatmentsResponse])

  return <JamSessionProviderImpl principal={props.principal}>{props.children}</JamSessionProviderImpl>
}
